import { Container, Graphics, Text } from 'pixi.js';
import type { Scene } from '../core/Scene';
import type { InputSnapshot } from '../input/actions';
import type { GameContext } from '../GameContext';
import { Palette } from '../render/Palette';
import { randomSeed } from '../sim/run/RunState';
import { ARTIFACT_DEFS } from '../sim/artifacts/ArtifactDefs';
import { highestClearedTier, tierLabel } from '../sim/run/tiers';
import { CharacterScene } from './CharacterScene';
import { RelicScene } from './RelicScene';
import { ForgeScene } from './ForgeScene';
import { RecordsScene } from './RecordsScene';
import { loadLastSeed, saveLastSeed } from '../meta/lastSeed';

interface MenuOption {
  label: () => string;
  enabled: () => boolean;
  action: () => void;
}

/**
 * Title screen. Fully controller-navigable: begin (random seed), repeat last
 * seed, or type a custom seed on keyboard.
 */
export class MenuScene implements Scene {
  readonly root = new Container();
  readonly blocksUpdateBelow = true;

  private focus = 0;
  private options: MenuOption[];
  private optionTexts: Text[] = [];
  private customSeed = '';
  private typing = false;
  private time = 0;
  private title: Text;
  private hintText: Text;

  constructor(private readonly ctx: GameContext) {
    const w = ctx.app.screen.width;
    const h = ctx.app.screen.height;

    this.root.addChild(new Graphics().rect(0, 0, w, h).fill(Palette.bg));

    // Faint descending-stairs motif.
    const deco = new Graphics();
    for (let i = 0; i < 7; i++) {
      deco.rect(w / 2 - 120 + i * 34, h / 2 - 40 + i * 26, 240 - i * 24, 5)
        .fill({ color: Palette.wallEdge, alpha: 0.14 - i * 0.015 });
    }
    this.root.addChild(deco);

    this.title = new Text({
      text: 'D E S C E N T',
      style: {
        fontFamily: 'monospace', fontSize: 64, fontWeight: 'bold',
        fill: Palette.hudText, letterSpacing: 10,
      },
    });
    this.title.anchor.set(0.5);
    this.title.position.set(w / 2, h / 2 - 160);
    this.root.addChild(this.title);

    const sub = new Text({
      text: 'the long stair has no bottom',
      style: { fontFamily: 'monospace', fontSize: 14, fill: Palette.hudDim, letterSpacing: 2 },
    });
    sub.anchor.set(0.5);
    sub.position.set(w / 2, h / 2 - 110);
    this.root.addChild(sub);

    const lastSeed = loadLastSeed();
    this.options = [
      {
        label: () => 'Begin the Descent',
        enabled: () => true,
        action: () => this.start(randomSeed()),
      },
      {
        label: () => (lastSeed ? `Repeat Seed  (${lastSeed})` : 'Repeat Seed  —'),
        enabled: () => lastSeed !== null,
        action: () => lastSeed && this.start(lastSeed),
      },
      {
        label: () => this.typing || this.customSeed
          ? `Custom Seed:  ${this.customSeed}${this.typing ? '_' : ''}`
          : 'Custom Seed  (type)',
        enabled: () => true,
        action: () => {
          if (this.customSeed && !this.typing) this.start(this.customSeed);
          else this.typing = true;
        },
      },
      {
        label: () => {
          const owned = this.ctx.profile.ownedCount;
          return `The Vault  (${owned}/${ARTIFACT_DEFS.length} artifacts)`;
        },
        enabled: () => true,
        action: () => this.ctx.scenes.push(new RelicScene(this.ctx, null)),
      },
      {
        label: () => `The Forge  (◇ ${this.ctx.profile.data.shards})`,
        enabled: () => true,
        action: () => this.ctx.scenes.push(new ForgeScene(this.ctx)),
      },
      {
        label: () => {
          const hc = highestClearedTier(this.ctx.profile.data);
          return hc >= 1 ? `Records  (${tierLabel(hc)} cleared)` : 'Records';
        },
        enabled: () => true,
        action: () => this.ctx.scenes.push(new RecordsScene(this.ctx)),
      },
      // Electron only (descentBridge = the shell's marker): the Deck build is
      // fullscreen and chromeless, so the menu must own its exit. Browsers
      // keep their own tab-close and ignore window.close() anyway.
      ...(window.descentBridge ? [{
        label: () => 'Quit',
        enabled: () => true,
        action: () => window.close(),
      }] : []),
    ];

    this.options.forEach((opt, i) => {
      const text = new Text({
        text: opt.label(),
        style: { fontFamily: 'monospace', fontSize: 18, fill: Palette.hudText },
      });
      text.anchor.set(0.5);
      text.position.set(w / 2, h / 2 - 10 + i * 44);
      this.optionTexts.push(text);
      this.root.addChild(text);
    });

    this.hintText = new Text({
      text: 'navigate  [↑↓ / dpad]      select  [E / Ⓐ]',
      style: { fontFamily: 'monospace', fontSize: 12, fill: Palette.hudDim },
    });
    this.hintText.anchor.set(0.5);
    this.hintText.position.set(w / 2, h - 48);
    this.root.addChild(this.hintText);
  }

  /** Seed chosen → the wizard: character+tier, then relics, then DESCEND. */
  private start(seed: string): void {
    saveLastSeed(seed);
    this.ctx.scenes.push(new CharacterScene(this.ctx, seed));
  }

  enter(): void {
    window.addEventListener('keydown', this.onKey);
  }

  exit(): void {
    window.removeEventListener('keydown', this.onKey);
  }

  private onKey = (e: KeyboardEvent): void => {
    if (!this.typing) return;
    if (e.key === 'Enter') {
      this.typing = false;
      if (this.customSeed) this.start(this.customSeed);
    } else if (e.key === 'Escape') {
      this.typing = false;
    } else if (e.key === 'Backspace') {
      this.customSeed = this.customSeed.slice(0, -1);
    } else if (/^[a-zA-Z0-9-]$/.test(e.key) && this.customSeed.length < 24) {
      this.customSeed += e.key;
    }
  };

  update(dt: number, input: InputSnapshot): void {
    this.time += dt;
    if (this.typing) {
      // Keyboard is captured for seed entry (raw keydown above) — but a pad
      // must still be able to leave, or a Deck user is soft-locked. Only
      // honor PAD presses here: kb confirm is KeyE, which types an 'e'.
      if (input.device === 'pad') {
        if (input.cancelPressed) this.typing = false;
        if (input.confirmPressed) {
          this.typing = false;
          if (this.customSeed) this.start(this.customSeed);
        }
      }
      return;
    }
    if (input.navY !== 0) {
      this.focus = (this.focus + input.navY + this.options.length) % this.options.length;
    }
    if (input.confirmPressed) {
      const opt = this.options[this.focus];
      if (opt && opt.enabled()) opt.action();
    }
  }

  render(_alpha: number): void {
    this.title.alpha = 0.85 + 0.15 * Math.sin(this.time * 1.4);
    this.options.forEach((opt, i) => {
      const text = this.optionTexts[i]!;
      text.text = (this.focus === i ? '▸ ' : '  ') + opt.label();
      text.style.fill = !opt.enabled()
        ? Palette.hudDim
        : this.focus === i ? Palette.playerCool : Palette.hudText;
    });
  }
}
